import React, { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { useOrgAdminAccess } from "hooks/useOrgAdminAccess";
import { db } from "services/firebase";

const LoopAnalyticsPage = () => {
  const { organizationId, userData } = useAuth();
  const { canAccess } = useOrgAdminAccess();
  const [loading, setLoading] = useState(true);
  const [augmentStats, setAugmentStats] = useState({ accept: 0, reject: 0, ignore: 0 });
  const [candidateStats, setCandidateStats] = useState({ pending: 0, approved: 0 });

  useEffect(() => {
    if (!organizationId || userData?.role !== "admin") {
      setLoading(false);
      return;
    }

    const load = async () => {
      const [augmentSnap, candidateSnap] = await Promise.all([
        db.collection("augment_feedback").where("organizationId", "==", organizationId).limit(200).get(),
        db.collection("promptCandidates").where("organizationId", "==", organizationId).limit(200).get(),
      ]);

      const augment = { accept: 0, reject: 0, ignore: 0 };
      augmentSnap.docs.forEach((doc) => {
        const action = doc.data()?.action;
        if (action in augment) augment[action] += 1;
      });

      const candidates = { pending: 0, approved: 0 };
      candidateSnap.docs.forEach((doc) => {
        const status = doc.data()?.status;
        if (status === "pending") candidates.pending += 1;
        if (status === "approved") candidates.approved += 1;
      });

      setAugmentStats(augment);
      setCandidateStats(candidates);
      setLoading(false);
    };

    load();
  }, [organizationId, userData?.role]);

  if (loading) return <CircularProgress />;

  if (!canAccess) {
    return (
      <Alert severity="warning">
        Loop analytics are available to organization admins only.
      </Alert>
    );
  }

  return (
    <Box>
      <DashboardPageHeader
        title="Loop performance"
        subtitle="Augment acceptance, prompt candidates, and refinement velocity."
      />
      <Stack spacing={2}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">Augment feedback</Typography>
          <Typography variant="body2">Accepted: {augmentStats.accept}</Typography>
          <Typography variant="body2">Rejected: {augmentStats.reject}</Typography>
          <Typography variant="body2">Ignored: {augmentStats.ignore}</Typography>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">Prompt candidates</Typography>
          <Typography variant="body2">Pending: {candidateStats.pending}</Typography>
          <Typography variant="body2">Approved: {candidateStats.approved}</Typography>
        </Paper>
      </Stack>
    </Box>
  );
};

export default LoopAnalyticsPage;
